/**
 * The operator, full-screen (ADR-124, schema v12).
 *
 * Shaped like the chat products people already live in: conversations on the
 * left, one open on the right, a composer that takes text and files — «+» to
 * attach, an arrow that sends and becomes a stop square while the reply is
 * being produced. Nothing here invents chat conventions — Enter sends,
 * Shift+Enter breaks the line, the newest conversation sits on top.
 *
 * What is deliberately *not* like a consumer chat: the header says plainly
 * which agent program answers and whether it is live, because "who am I
 * talking to" is a configuration fact on this platform, and a chat that hides
 * it would let a canned reply impersonate an agent.
 */

import { useEffect, useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ApiError, isTaskNotFoundError } from '../../client/api.js';
import type { HomeThreadMessage, OperatorChatAttachmentView, OperatorChatMessage, OperatorChatThread } from '../../client/types.js';
import { parseTeamDocument, serializeTeamDocument, writeOperatorModel, writeOperatorProgram } from '../../logic/team-editor.js';
import { describeSaveFailure, useApplyTeamDocument } from '../apply-team.js';
import {
  useAttachmentUrl,
  useConsoleMutation,
  useHomeThread,
  useOperatorChat,
  useOperatorChats,
  useOperatorStatus,
  useRoles,
  useTaskChat,
  useProposals,
} from '../queries.js';
import { useStore } from '../store-context.js';
import { Orb } from '../components/orb.js';
import { QueryPhase } from '../components/state.js';
import { Button, Flash, IconButton } from '@primer/react';
import { ArrowUpIcon, ChevronDownIcon, PlusIcon, SquareFillIcon, UnmuteIcon } from '@primer/octicons-react';
import { TeamChangeProposalReview } from '../components/team-change-proposal.js';

export function OperatorScreen(): ReactNode {
  const { id } = useParams();
  const chatId = id ?? null;
  const navigate = useNavigate();
  const chats = useOperatorChats();
  const roles = useRoles();

  const create = useConsoleMutation<void, { id: string }>(
    (data, context) => data.createOperatorChat(context),
    [['operator-chats']],
  );
  const openAgent = useConsoleMutation<string, { id: string }>(
    (data, context, roleId) => data.openRoleChat(roleId, context),
    [['operator-chats']],
  );

  /*
   * Landing on `#/operator` opens the newest conversation, the way every chat
   * product does. With none yet, the empty state offers to start one — it
   * does not start one silently, because an unvisited screen must not write.
   */
  useEffect(() => {
    if (chatId !== null || chats.data === undefined) return;
    const newest = chats.data[0];
    if (newest !== undefined) void navigate(`/operator/${newest.id}`, { replace: true });
  }, [chatId, chats.data, navigate]);

  const startChat = (): void => {
    create.mutate(undefined, {
      onSuccess: (chat) => void navigate(`/operator/${chat.id}`),
    });
  };

  return (
    <div className="operator-screen">
      <aside className="chat-sidebar" aria-label="Conversations">
        <Button variant="primary" className="chat-new" onClick={startChat} disabled={create.isPending}>
          {create.isPending ? 'Starting…' : '+ New chat'}
        </Button>
        <QueryPhase isPending={chats.isPending} error={chats.error} source="operator chats" rows={3} />
        <ul className="chat-list">
          {(chats.data ?? [])
            .filter((chat) => chat.roleId === null && chat.forkOfRole === null)
            .map((chat) => (
              <ChatRow key={chat.id} id={chat.id} title={chat.title} at={chat.lastMessageAt} active={chat.id === chatId} />
            ))}
        </ul>

        {/*
          The team's own agents: one conversation each, into the same thread
          its task cards arrive in. Opening it is how you meet the agent —
          brief it, ask what it is doing, watch it think.
        */}
        {(roles.data ?? []).filter((role) => role.roleId !== 'main-operator').length === 0 ? null : (
          <>
            <p className="chat-section">Agents</p>
            <ul className="chat-list">
              {(roles.data ?? [])
                .filter((role) => role.roleId !== 'main-operator')
                .map((role) => {
                  const chat = (chats.data ?? []).find((entry) => entry.roleId === role.roleId) ?? null;
                  const forks = (chats.data ?? []).filter((entry) => entry.forkOfRole === role.roleId);
                  return (
                    <li key={role.roleId} className={chat !== null && chat.id === chatId ? 'chat-row chat-row-active' : 'chat-row'}>
                      <button
                        type="button"
                        className="chat-open"
                        disabled={openAgent.isPending}
                        onClick={() => {
                          if (chat !== null) {
                            void navigate(`/operator/${chat.id}`);
                            return;
                          }
                          openAgent.mutate(role.roleId, {
                            onSuccess: (opened) => void navigate(`/operator/${opened.id}`),
                          });
                        }}
                      >
                        <span className="chat-title">{role.roleId}</span>
                        {chat !== null ? <span className="chat-when">{chatTime(chat.lastMessageAt)}</span> : null}
                      </button>
                      {/* The agent's disposable forks nest under it — subthreads,
                          not conversations of their own in the operator's list. */}
                      {forks.length === 0 ? null : (
                        <ul className="chat-sublist">
                          {forks.map((fork) => (
                            <ChatRow
                              key={fork.id}
                              id={fork.id}
                              title="fork"
                              at={fork.lastMessageAt}
                              active={fork.id === chatId}
                              sub
                            />
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
            </ul>
          </>
        )}
      </aside>

      <section className="chat-main" aria-label="Conversation">
        {(() => {
          const activeChat = (chats.data ?? []).find((chat) => chat.id === chatId) ?? null;
          const activeRole = activeChat?.roleId ?? null;
          const forkOf = activeChat?.forkOfRole ?? null;
          /*
           * The operator's header — program picker included — belongs only to
           * the operator's own chats. A chat with an agent shows who you are
           * talking to; the agent's program is configured on its card, and a
           * picker here would silently change the *operator*, not the agent.
           */
          if (activeRole !== null) {
            return (
              <header className="operator-head">
                <p className="operator-live">
                  <strong>{activeRole}</strong>
                  <span> — the agent’s working thread: its tasks, its replies, and this conversation</span>
                </p>
                <div className="operator-controls">
                  <ForkThreadButton roleId={activeRole} />
                  <ResetSessionButton />
                </div>
              </header>
            );
          }
          if (forkOf !== null) {
            return (
              <header className="operator-head">
                <p className="operator-live">
                  <strong>{forkOf} — fork</strong>
                  <span> — a disposable copy of the agent’s context; the working thread never sees this. Delete it when done.</span>
                </p>
                <div className="operator-controls">
                  <DeleteForkButton chatId={chatId} />
                </div>
              </header>
            );
          }
          return <OperatorHeader />;
        })()}
        {chatId === null ? (
          <div className="chat-empty">
            <p>Ask why something is stuck, or hand over a file.</p>
            <Button variant="primary" onClick={startChat} disabled={create.isPending}>
              Start a conversation
            </Button>
          </div>
        ) : (
          <Conversation chatId={chatId} />
        )}
      </section>
    </div>
  );
}

function ChatRow({
  id,
  title,
  at,
  active,
  sub = false,
}: {
  id: string;
  title: string;
  at: string;
  active: boolean;
  /** A nested subthread row (an agent's fork) — indented under its parent. */
  sub?: boolean;
}): ReactNode {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const remove = useConsoleMutation<string, void>(
    (data, context, chatId) => data.deleteOperatorChat(chatId, context),
    [['operator-chats']],
  );

  return (
    <li className={[active ? 'chat-row chat-row-active' : 'chat-row', sub ? 'chat-row-sub' : ''].join(' ').trim()}>
      <button type="button" className="chat-open" onClick={() => void navigate(`/operator/${id}`)}>
        <span className="chat-title">{title}</span>
        <span className="chat-when">{chatTime(at)}</span>
      </button>
      {/* Two presses, not a browser dialog: the first arms, the second acts. */}
      <button
        type="button"
        className="chat-delete"
        aria-label={confirming ? `really delete ${title}` : `delete ${title}`}
        onClick={() => {
          if (!confirming) {
            setConfirming(true);
            window.setTimeout(() => setConfirming(false), 3_000);
            return;
          }
          remove.mutate(id, {
            onSuccess: () => {
              if (active) void navigate('/operator');
            },
          });
        }}
      >
        {confirming ? 'sure?' : '×'}
      </button>
    </li>
  );
}

/**
 * Who answers here, and the one control that changes it.
 *
 * The program picker writes `mainOperator.harness` through the same
 * draft-and-apply path as every other team change — the operator's identity
 * is team configuration, and it is edited where it is felt.
 */
function OperatorHeader(): ReactNode {
  const status = useOperatorStatus();
  const store = useStore();
  const apply = useApplyTeamDocument();
  const [choice, setChoice] = useState<string | null>(null);
  const [modelChoice, setModelChoice] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [harnesses, setHarnesses] = useState<
    readonly { id: string; label: string; models?: readonly { id: string; label: string }[] }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    void store.data
      .harnesses()
      .then((listed) => {
        if (!cancelled) setHarnesses(listed);
      })
      .catch(() => {
        // The select falls back to what the status names.
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  if (status.data === undefined) return <header className="chat-head" />;

  const current = status.data.program ?? '';
  const selected = choice ?? current;
  const currentModel = status.data.model ?? '';
  const selectedModel = modelChoice ?? currentModel;
  const dirty = selected !== current || selectedModel.trim() !== currentModel;

  const onApply = (): void => {
    setApplying(true);
    setProblem(null);
    apply((source) =>
      serializeTeamDocument(
        writeOperatorModel(writeOperatorProgram(parseTeamDocument(source), selected), selectedModel),
      ),
    )
      .then(() => {
        setChoice(null);
        setModelChoice(null);
      })
      .catch((error: unknown) => {
        const described = describeSaveFailure(error);
        setProblem([described.title, ...described.lines].join(' — '));
      })
      .finally(() => setApplying(false));
  };

  return (
    <header className="chat-head">
      <div className="chat-head-status">
        <span className={status.data.live ? 'lamp lamp-live' : 'lamp'} aria-hidden />
        <strong>{status.data.program === null ? 'Built-in replies' : (status.data.label ?? status.data.program)}</strong>
        {status.data.model === null ? null : <span className="hint">{status.data.model}</span>}
        <span className="hint">
          {status.data.program === null
            ? 'no agent behind this chat yet — pick a program'
            : status.data.live
              ? 'agent running'
              : 'starts on your next message'}
        </span>
      </div>
      <div className="chat-head-controls">
        <ResetSessionButton />
        <label className="chat-program">
          <span className="editor-label">Program</span>
          <select value={selected} onChange={(event) => setChoice(event.target.value)}>
            <option value="">Built-in replies (no model)</option>
            {harnesses.map((harness) => (
              <option key={harness.id} value={harness.id}>
                {harness.label}
              </option>
            ))}
            {current.length > 0 && harnesses.every((harness) => harness.id !== current) ? (
              <option value={current}>{current}</option>
            ) : null}
          </select>
        </label>
        {selected === '' ? null : (
          <label className="chat-program">
            <span className="editor-label">Model</span>
            {(() => {
              const models = harnesses.find((harness) => harness.id === selected)?.models ?? [];
              // No list to offer (harness absent, cache unreadable): free-form
              // input still lets the person name a model by hand.
              if (models.length === 0) {
                return (
                  <input
                    type="text"
                    value={selectedModel}
                    placeholder="harness default"
                    size={20}
                    onChange={(event) => setModelChoice(event.target.value)}
                  />
                );
              }
              return (
                <select value={selectedModel} onChange={(event) => setModelChoice(event.target.value)}>
                  <option value="">Harness default</option>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.label}
                    </option>
                  ))}
                  {selectedModel.length > 0 && models.every((model) => model.id !== selectedModel) ? (
                    <option value={selectedModel}>{selectedModel}</option>
                  ) : null}
                </select>
              );
            })()}
          </label>
        )}
        {dirty ? (
          <Button variant="primary" onClick={onApply} disabled={applying}>
            {applying ? 'Applying…' : 'Apply'}
          </Button>
        ) : null}
      </div>
      {problem !== null ? (
        <p className="notice notice-danger chat-head-problem" role="alert">
          {problem}
        </p>
      ) : null}
    </header>
  );
}

/**
 * The emergency exit: forget the model context, keep everything else.
 *
 * Two presses — the first arms — because the cost, while bounded (the home,
 * its files and the chat history stay), is a conversation's accumulated
 * context, and that is not a thing to drop on a stray click.
 */
/** Start a disposable side-conversation seeded from the agent's live thread. */
function ForkThreadButton({ roleId }: { roleId: string }): ReactNode {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<string | null>(null);
  const fork = useConsoleMutation<string, { id: string }>(
    (data, context, role) => data.forkRoleChat(role, context),
    [['operator-chats']],
  );
  return (
    <>
      <Button
        disabled={fork.isPending}
        onClick={() =>
          fork.mutate(roleId, {
            onSuccess: (chat) => void navigate(`/operator/${chat.id}`),
            onError: (error) => setProblem(error instanceof Error ? error.message : String(error)),
          })
        }
      >
        {fork.isPending ? 'Forking…' : 'Fork thread'}
      </Button>
      {problem === null ? null : (
        <span className="hint" role="alert">
          {problem}
        </span>
      )}
    </>
  );
}

/** Delete this fork — chat, thread pointer and all — and return to the list. */
function DeleteForkButton({ chatId }: { chatId: string | null }): ReactNode {
  const navigate = useNavigate();
  const [arming, setArming] = useState(false);
  const remove = useConsoleMutation<string, void>(
    (data, context, chat) => data.deleteOperatorChat(chat, context),
    [['operator-chats']],
  );
  if (chatId === null) return null;
  return (
    <Button
      variant="danger"
      disabled={remove.isPending}
      onClick={() => {
        if (!arming) {
          setArming(true);
          setTimeout(() => setArming(false), 4000);
          return;
        }
        remove.mutate(chatId, { onSuccess: () => void navigate('/operator') });
      }}
    >
      {arming ? 'Really delete?' : 'Delete fork'}
    </Button>
  );
}

function ResetSessionButton(): ReactNode {
  const { id } = useParams();
  const chatId = id ?? null;
  const [arming, setArming] = useState(false);
  const reset = useConsoleMutation<string, void>(
    (data, context, chat) => data.resetOperatorChat(chat, context),
    [['operator-chat', chatId], ['home-thread', chatId], ['operator-chats'], ['operator-status']],
  );

  if (chatId === null) return null;

  return (
    <Button variant="danger"
      disabled={reset.isPending}
      onClick={() => {
        if (!arming) {
          setArming(true);
          window.setTimeout(() => setArming(false), 3_000);
          return;
        }
        setArming(false);
        reset.mutate(chatId);
      }}
    >
      {reset.isPending ? 'Resetting…' : arming ? 'Sure? Context will be lost' : 'Reset session'}
    </Button>
  );
}

export function Conversation({ chatId }: { chatId: string }): ReactNode {
  const thread = useOperatorChat(chatId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [awayFromBottom, setAwayFromBottom] = useState(false);

  /*
   * A chat with an agent (or a fork of one) shows the agent's WORKING thread,
   * read from its harness session log — its tasks arriving, its replies, and
   * this conversation, all in one lane. The Console-recorded messages remain
   * the lane for operator chats, where no harness thread exists.
   */
  const laneRole = thread.data?.chat.roleId ?? thread.data?.chat.forkOfRole ?? null;
  const homeLane = useHomeThread(chatId, laneRole !== null);
  const durableMessages = laneRole === null ? [] : thread.data?.messages ?? [];
  const durableHumanMessages = durableMessages.filter((message) => message.author === 'human');
  const roleLane = mergeRoleChatLane(homeLane.data ?? [], durableMessages);
  const messageCount = laneRole !== null
    ? roleLane.length
    : thread.data?.messages.length ?? 0;

  /*
   * A reply being produced shows as one. `pending` carries the narration —
   * commands being run, answer text as it forms — and the dots stand in
   * only before the first narrated word. Without this, a working operator
   * and a dead one looked identical for as long as the model took — which
   * got reported, fairly, as «не работает».
   */
  const pending = thread.data?.pending ?? null;
  /*
   * Dots exactly while a run is in flight — the server sets `pending` to '' the
   * moment a message is delivered and deletes it when the run ends (or after the
   * 5-minute ceiling, or a restart). Reading a message lane instead — operator
   * entries OR the home lane — leaves the dots stuck forever whenever a reply
   * never lands (a placed agent whose answer went to its session, a run the
   * daemon lost): the last turn stays the person's with no run to end it.
   */
  const awaiting = pending === '';

  /*
   * A message a person just sent shows at once — before the harness has written
   * it into the session the home lane reads. Without it a role chat looked dead
   * for the poll it took to appear, while the operator chat (recorded entries)
   * showed it instantly. The stand-in drops when the real one lands (the lane
   * grows), with a timer covering a send that never does.
   */
  const [optimistic, setOptimistic] = useState<readonly OptimisticOperatorMessage[]>([]);
  const optSeq = useRef(0);
  useEffect(() => {
    if (optimistic.length === 0) return;
    setOptimistic((current) => optimisticMessagesWithoutDurableMatches(current, durableHumanMessages));
  }, [thread.data?.messages, optimistic.length]);
  const handleSent = (text: string, files: readonly File[]): (() => void) => {
    const id = `opt-${String((optSeq.current += 1))}`;
    setOptimistic((prev) => [...prev, {
      id, text, files, durableIdsAtSend: new Set(durableHumanMessages.map((message) => message.id)),
    }]);
    return () => setOptimistic((prev) => prev.filter((entry) => entry.id !== id));
  };

  /*
   * Pinned to the bottom while new messages arrive, unless the person has
   * scrolled up to read — yanking the view away from an old message because
   * the agent spoke is how chat UIs teach people not to scroll.
   */
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    const pane = scrollRef.current;
    if (pane === null) return;
    /*
     * Opening a conversation starts at its newest message: the first load of
     * a long thread lands with scrollTop at 0, which the near-bottom guard
     * below would read as «the person scrolled up» and refuse to pin.
     */
    if (openedRef.current !== chatId && messageCount > 0) {
      openedRef.current = chatId;
      pane.scrollTo({ top: pane.scrollHeight });
      setAwayFromBottom(false);
      return;
    }
    const nearBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight < 240;
    if (nearBottom) pane.scrollTo({ top: pane.scrollHeight });
    setAwayFromBottom(!nearBottom);
  }, [chatId, messageCount, optimistic.length, awaiting, pending]);

  /*
   * The jump-down arrow: shown once the person has scrolled up past the
   * auto-pin threshold, hidden again at the bottom. Scroll is passive and
   * per-pane, so a long thread costs nothing extra.
   */
  useEffect(() => {
    const pane = scrollRef.current;
    if (pane === null) return undefined;
    const onScroll = (): void => {
      setAwayFromBottom(pane.scrollHeight - pane.scrollTop - pane.clientHeight > 240);
    };
    onScroll();
    pane.addEventListener('scroll', onScroll, { passive: true });
    return () => pane.removeEventListener('scroll', onScroll);
  }, [chatId]);

  const jumpToBottom = (): void => {
    const pane = scrollRef.current;
    if (pane === null) return;
    const distance = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    // Smooth only over a short hop: gliding through a 90k-pixel thread takes
    // seconds and reads as a broken button. Three screens is the cutoff.
    const behavior = reduced || distance > pane.clientHeight * 3 ? 'auto' : 'smooth';
    pane.scrollTo({ top: pane.scrollHeight, behavior });
  };

  return (
    <>
      <div className="chat-scroll" ref={scrollRef}>
        <QueryPhase isPending={thread.isPending} error={thread.error} source="operator chat" rows={4} />
        <ol className="chat-messages">
          {laneRole !== null
            ? roleLane.map((entry, index) => entry.kind === 'home'
              ? <HomeBubble key={`home-${entry.message.at}-${String(index)}`} message={entry.message} roleId={laneRole} />
              : <Bubble key={entry.message.id} message={entry.message} />)
            : (thread.data?.messages ?? []).map((message) => (
                <Bubble key={message.id} message={message} />
              ))}
          {laneRole !== null
            ? optimistic.map((entry) => <OptimisticBubble key={entry.id} message={entry} />)
            : null}
          {pending !== null && pending !== '' ? (
            <li className="chat-bubble chat-bubble-operator chat-bubble-live" aria-label="a reply is being written">
              <p className="chat-bubble-meta">
                <Orb size={20} state="working" />
                <span className="chat-bubble-author">{thread.data?.chat.roleId ?? 'operator'}</span>
                <span className="chat-bubble-when">now</span>
              </p>
              <p className="chat-bubble-body">
                {pending}
                <span className="live-caret" aria-hidden />
              </p>
            </li>
          ) : awaiting ? (
            <li className="chat-bubble chat-bubble-operator chat-typing" aria-label="the operator is working on a reply">
              <Orb size={48} state="composing" />
              <span className="chat-typing-word">Thinking…</span>
            </li>
          ) : null}
        </ol>
        {laneRole !== null && homeLane.data !== undefined && messageCount === 0 && optimistic.length === 0 ? (
          <p className="chat-empty-hint">
            Loading the agent’s history… If this is its first run, the thread will stay empty until a task or message arrives.
          </p>
        ) : laneRole === null && thread.data !== undefined && thread.data.messages.length === 0 ? (
          <p className="chat-empty-hint">Nothing said yet. The operator reads the board, the runs and this machine.</p>
        ) : null}
      </div>
      {awayFromBottom ? (
        <button type="button" className="chat-jump" onClick={jumpToBottom} aria-label="Scroll to the latest message">
          <ChevronDownIcon size={16} />
        </button>
      ) : null}
      <Composer target={{ kind: 'operator', chatId }} busy={pending !== null || awaiting} onSent={handleSent} />
    </>
  );
}

/** A bounded planning-chat lane that shares the normal composer without full-screen scroll bookkeeping. */
export function EmbeddedConversation({ chatId }: { chatId: string }): ReactNode {
  const thread = useOperatorChat(chatId);
  const pending = thread.data?.pending ?? null;
  const messageCount = thread.data?.messages.length ?? 0;
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);
  const [awayFromBottom, setAwayFromBottom] = useState(false);

  useEffect(() => {
    const pane = scrollRef.current;
    if (pane === null) return;
    if (pinnedToBottom.current) pane.scrollTo({ top: pane.scrollHeight });
  }, [chatId, messageCount, pending]);

  useEffect(() => {
    const pane = scrollRef.current;
    if (pane === null) return undefined;
    pinnedToBottom.current = true;
    const onScroll = (): void => {
      const away = pane.scrollHeight - pane.scrollTop - pane.clientHeight > 240;
      pinnedToBottom.current = !away;
      setAwayFromBottom(away);
    };
    pane.scrollTo({ top: pane.scrollHeight });
    onScroll();
    pane.addEventListener('scroll', onScroll, { passive: true });
    return () => pane.removeEventListener('scroll', onScroll);
  }, [chatId]);

  const jumpToBottom = (): void => {
    const pane = scrollRef.current;
    if (pane === null) return;
    pinnedToBottom.current = true;
    setAwayFromBottom(false);
    const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    pane.scrollTo({ top: pane.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
  };
  return (
    <>
      <div className="chat-scroll" ref={scrollRef}>
        <QueryPhase isPending={thread.isPending} error={thread.error} source="planning chat" rows={4} />
        <ol className="chat-messages">
          {(thread.data?.messages ?? []).map((message) => <Bubble key={message.id} message={message} />)}
          {pending !== null ? (
            <li className={`chat-bubble chat-bubble-operator ${pending.length === 0 ? 'chat-typing' : 'chat-bubble-live'}`} aria-label="a reply is being written">
              {pending.length === 0 ? <><Orb size={48} state="composing" /><span className="chat-typing-word">Thinking…</span></> : <><p className="chat-bubble-meta"><Orb size={20} state="working" /><span className="chat-bubble-author">operator</span><span className="chat-bubble-when">now</span></p><p className="chat-bubble-body">{pending}<span className="live-caret" aria-hidden /></p></>}
            </li>
          ) : null}
        </ol>
      </div>
      {awayFromBottom ? (
        <button type="button" className="chat-jump" onClick={jumpToBottom} aria-label="Scroll to the latest message">
          <ChevronDownIcon size={16} />
        </button>
      ) : null}
      <Composer target={{ kind: 'operator', chatId }} busy={pending !== null} onSent={() => () => undefined} />
    </>
  );
}

/**
 * The one conversation owned by a task. It deliberately reuses the operator
 * bubbles and composer, while omitting the global conversation list and all
 * session-management controls.
 */
export function appendUniqueMessages(
  existing: readonly OperatorChatMessage[],
  incoming: readonly OperatorChatMessage[],
): readonly OperatorChatMessage[] {
  const seen = new Set(existing.map((message) => message.id));
  return [...existing, ...incoming.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  })];
}

type RoleChatLaneEntry =
  | { readonly kind: 'home'; readonly message: HomeThreadMessage }
  | { readonly kind: 'durable'; readonly message: OperatorChatMessage };

/** Merge working context with durable sends in time order; SQL owns chat attachments. */
export function mergeRoleChatLane(
  home: readonly HomeThreadMessage[],
  durable: readonly OperatorChatMessage[],
): readonly RoleChatLaneEntry[] {
  const consumed = new Set<number>();
  const duplicate = (message: HomeThreadMessage): boolean => {
    if (message.at.length === 0) return false;
    const homeAt = Date.parse(message.at);
    if (!Number.isFinite(homeAt)) return false;
    const expectedAuthor = message.author === 'input' ? 'human' : 'operator';
    const match = durable.findIndex((candidate, index) => !consumed.has(index)
      && candidate.author === expectedAuthor
      && candidate.body === message.text
      && Math.abs(Date.parse(candidate.at) - homeAt) <= 30_000);
    if (match < 0) return false;
    consumed.add(match);
    return true;
  };
  const entries: { readonly entry: RoleChatLaneEntry; readonly at: number; readonly order: number }[] = [];
  home.forEach((message, order) => {
    // Placed harnesses persist the complete compiled role brief as their user
    // turn. It is execution context, not something the person sent, and must
    // never leak into the visible conversation (or repeat after every run).
    if (message.author === 'input'
      && message.text.startsWith('You are «')
      && message.text.includes('an agent on an autonomous team.')) return;
    if (!duplicate(message)) entries.push({ entry: { kind: 'home', message }, at: Date.parse(message.at), order });
  });
  durable.forEach((message, index) => entries.push({
    entry: { kind: 'durable', message }, at: Date.parse(message.at), order: home.length + index,
  }));
  entries.sort((left, right) => {
    const leftAt = Number.isFinite(left.at) ? left.at : Number.MAX_SAFE_INTEGER;
    const rightAt = Number.isFinite(right.at) ? right.at : Number.MAX_SAFE_INTEGER;
    return leftAt - rightAt || left.order - right.order;
  });
  return entries.map(({ entry }) => entry);
}

export function prependUniqueMessages(
  incoming: readonly OperatorChatMessage[],
  existing: readonly OperatorChatMessage[],
): readonly OperatorChatMessage[] {
  const incomingIds = new Set(incoming.map((message) => message.id));
  // The freshly read page is authoritative for overlaps: delivery metadata
  // may have changed since the same boundary bubble was first rendered.
  return [...incoming, ...existing.filter((message) => !incomingIds.has(message.id))];
}

export function settledMessagesMissingFrom(
  previous: readonly OperatorChatMessage[],
  current: readonly OperatorChatMessage[],
): readonly OperatorChatMessage[] {
  const currentIds = new Set(current.map((message) => message.id));
  return previous.filter((message) => {
    if (currentIds.has(message.id)) return false;
    return message.delivery === undefined
      || message.delivery.state === 'completed'
      || message.delivery.state === 'failed';
  });
}

export function withoutDuplicateTaskDeliveryFailures(
  messages: readonly OperatorChatMessage[],
): readonly OperatorChatMessage[] {
  return messages.filter((message, index) => {
    if (message.author !== 'system' || message.body.length === 0) return true;
    const previous = messages[index - 1];
    return previous?.author !== 'human'
      || previous.delivery?.state !== 'failed'
      || previous.delivery.error !== message.body;
  });
}

export function cursorAfterOlderPage(
  requestedWindow: string,
  currentWindow: string,
  pageCursor: number | null,
): number | null | undefined {
  return requestedWindow === currentWindow ? pageCursor : undefined;
}

export const TASK_CHAT_FIRST_PROMPT = `Кратко введи меня в контекст этой задачи — карточку и логи я не читал. Ответь по пунктам:
1. Что нужно получить в итоге.
2. Что уже сделано.
3. Что именно сейчас блокирует завершение.
4. Какие есть варианты решения и риски каждого.
5. Какой вариант ты рекомендуешь и почему.

Не пересказывай логи и длинную историю — только факты, необходимые для решения. До 10 коротких пунктов.`;

export function reconcileSeededDraft(current: string, previousSeed: string, nextSeed: string): string {
  return current === previousSeed ? nextSeed : current;
}

export function TaskConversation({ taskBindingId }: { taskBindingId: string }): ReactNode {
  const store = useStore();
  const taskChatScope = `${store.currentTeamId ?? ''}:${taskBindingId}`;
  const openedRef = useRef<string | null>(null);
  const [ensuredScope, setEnsuredScope] = useState<string | null>(null);
  const ensure = useConsoleMutation<string, OperatorChatThread>(
    (data, context, id) => data.ensureTaskChat(id, context),
    [['task-chat', taskBindingId]],
  );
  const thread = useTaskChat(taskBindingId, ensuredScope === taskChatScope);
  const teamChanges = useProposals({ kind: 'team-change', sourceTaskBindingId: taskBindingId });
  const retry = useConsoleMutation<{ readonly messageId: string }, void>(
    (data, context, input) => data.retryTaskChatMessage(taskBindingId, input.messageId, context),
    [['task-chat', taskBindingId]],
  );
  const current = thread.data ?? (ensuredScope === taskChatScope ? ensure.data : undefined);
  const pending = current?.pending ?? null;
  const latestMessageId = current?.messages.at(-1)?.id ?? null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const [olderMessages, setOlderMessages] = useState<readonly OperatorChatMessage[]>([]);
  const [olderCursorOverride, setOlderCursorOverride] = useState<number | null | undefined>(undefined);
  const [olderPending, setOlderPending] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);
  const preserveScrollHeight = useRef<number | null>(null);
  const previousCurrentMessages = useRef<readonly OperatorChatMessage[]>([]);
  const latestScopeRef = useRef(taskChatScope);
  const liveWindowKey = `${taskChatScope}:${String(current?.olderCursor ?? 'none')}`;
  const liveWindowKeyRef = useRef(liveWindowKey);
  latestScopeRef.current = taskChatScope;
  liveWindowKeyRef.current = liveWindowKey;

  useEffect(() => {
    if (openedRef.current === taskChatScope) return;
    openedRef.current = taskChatScope;
    setEnsuredScope(null);
    ensure.mutate(taskBindingId, { onSuccess: () => setEnsuredScope(taskChatScope) });
  }, [ensure, taskBindingId, taskChatScope]);

  useEffect(() => {
    setOlderMessages([]);
    setOlderCursorOverride(undefined);
    setOlderPending(false);
    setOlderError(null);
    preserveScrollHeight.current = null;
    previousCurrentMessages.current = [];
  }, [taskChatScope]);

  const currentMessages = current?.messages ?? [];
  useEffect(() => {
    const disappearedMessages = settledMessagesMissingFrom(previousCurrentMessages.current, currentMessages);
    if (disappearedMessages.length > 0) {
      setOlderMessages((existing) => appendUniqueMessages(existing, disappearedMessages));
    }
    previousCurrentMessages.current = currentMessages;
  }, [currentMessages]);

  useEffect(() => {
    // The live 500-message window advanced. Re-expose its new cursor even if
    // the user had previously loaded all older pages, so a mutable boundary
    // message is read again with its final delivery state.
    setOlderCursorOverride(undefined);
  }, [current?.olderCursor]);

  useLayoutEffect(() => {
    const previousHeight = preserveScrollHeight.current;
    const pane = scrollRef.current;
    if (previousHeight === null || pane === null) return;
    pane.scrollTop += pane.scrollHeight - previousHeight;
    preserveScrollHeight.current = null;
  }, [olderMessages]);

  useEffect(() => {
    const pane = scrollRef.current;
    if (pane !== null && pinnedToBottom.current) pane.scrollTo({ top: pane.scrollHeight });
  }, [latestMessageId, pending, taskBindingId]);

  useEffect(() => {
    const pane = scrollRef.current;
    if (pane === null) return undefined;
    pinnedToBottom.current = true;
    const onScroll = (): void => {
      const away = pane.scrollHeight - pane.scrollTop - pane.clientHeight > 240;
      pinnedToBottom.current = !away;
      setAwayFromBottom(away);
    };
    pane.scrollTo({ top: pane.scrollHeight });
    onScroll();
    pane.addEventListener('scroll', onScroll, { passive: true });
    return () => pane.removeEventListener('scroll', onScroll);
  }, [taskBindingId]);

  const jumpToBottom = (): void => {
    const pane = scrollRef.current;
    if (pane === null) return;
    pinnedToBottom.current = true;
    setAwayFromBottom(false);
    const distance = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const behavior = reduced || distance > pane.clientHeight * 3 ? 'auto' : 'smooth';
    pane.scrollTo({ top: pane.scrollHeight, behavior });
  };

  const olderCursor = olderCursorOverride === undefined ? current?.olderCursor ?? null : olderCursorOverride;
  const messages = withoutDuplicateTaskDeliveryFailures([...olderMessages, ...currentMessages]);
  const loadOlder = async (): Promise<void> => {
    if (olderCursor === null || olderPending) return;
    const pane = scrollRef.current;
    preserveScrollHeight.current = pane?.scrollHeight ?? null;
    setOlderPending(true);
    setOlderError(null);
    const requestedScope = taskChatScope;
    const requestedWindow = liveWindowKey;
    try {
      const page = await store.data.taskChat(taskBindingId, olderCursor);
      if (latestScopeRef.current !== requestedScope) {
        preserveScrollHeight.current = null;
        return;
      }
      setOlderMessages((existing) => prependUniqueMessages(page.messages, existing));
      setOlderCursorOverride(cursorAfterOlderPage(requestedWindow, liveWindowKeyRef.current, page.olderCursor));
    } catch (error) {
      if (latestScopeRef.current !== requestedScope) return;
      preserveScrollHeight.current = null;
      setOlderError(String(error));
    } finally {
      if (latestScopeRef.current === requestedScope) setOlderPending(false);
    }
  };

  if (ensure.isError) {
    const deleted = isTaskNotFoundError(ensure.error);
    return (
      <div className="task-chat-problem">
        <Flash variant="danger" role="alert">
          {deleted ? 'This task no longer exists. Task Chat is unavailable.' : `Task Chat could not be opened: ${String(ensure.error)}`}
        </Flash>
        {deleted ? null : (
          <Button
            onClick={() => ensure.mutate(taskBindingId, { onSuccess: () => setEnsuredScope(taskChatScope) })}
            disabled={ensure.isPending}
          >Retry</Button>
        )}
      </div>
    );
  }

  if (isTaskNotFoundError(thread.error)) {
    return (
      <div className="task-chat-problem">
        <Flash variant="danger" role="alert">This task no longer exists. Task Chat is unavailable.</Flash>
      </div>
    );
  }

  return (
    <>
      <div className="chat-scroll" ref={scrollRef}>
        <QueryPhase
          isPending={ensure.isPending || (ensuredScope === taskChatScope && thread.isPending)}
          error={thread.error}
          source="Task Chat"
          verb={ensuredScope === taskChatScope ? 'Reading' : 'Opening'}
          rows={4}
        />
        <ol className="chat-messages">
          {olderCursor === null ? null : (
            <li className="chat-history-load">
              <Button size="small" onClick={() => void loadOlder()} disabled={olderPending}>
                {olderPending ? 'Loading…' : 'Load older messages'}
              </Button>
              {olderError === null ? null : <span role="alert">Could not load older messages: {olderError}</span>}
            </li>
          )}
          {messages.map((message) => (
            <Bubble
              key={message.id}
              message={message}
              retryPending={retry.isPending}
              {...(message.delivery?.state === 'failed'
                ? { onRetry: () => retry.mutate({ messageId: message.delivery?.messageId ?? '' }) }
                : {})}
            />
          ))}
          {pending !== null ? (
            <li className={`chat-bubble chat-bubble-operator ${pending.length === 0 ? 'chat-typing' : 'chat-bubble-live'}`} aria-label="a reply is being written">
              {pending.length === 0
                ? <><Orb size={48} state="composing" /><span className="chat-typing-word">Thinking…</span></>
                : <><p className="chat-bubble-meta"><Orb size={20} state="working" /><span className="chat-bubble-author">operator</span><span className="chat-bubble-when">now</span></p><p className="chat-bubble-body">{pending}<span className="live-caret" aria-hidden /></p></>}
            </li>
          ) : null}
        </ol>
        {teamChanges.isPending && teamChanges.data === undefined ? null : (teamChanges.data ?? []).length === 0 ? null : (
          <section className="task-chat-proposals" aria-label="Task Chat proposals">
            {(teamChanges.data ?? []).map((proposal) => <TeamChangeProposalReview key={proposal.id} proposal={proposal} />)}
          </section>
        )}
        {teamChanges.error === null ? null : <Flash variant="danger" role="alert">Could not read this task’s proposals: {teamChanges.error.message}</Flash>}
        {current !== undefined && current.messages.length === 0 && pending === null ? (
          <p className="chat-empty-hint">Ask the Main Operator about this task.</p>
        ) : null}
      </div>
      {awayFromBottom ? (
        <button type="button" className="chat-jump" onClick={jumpToBottom} aria-label="Scroll to the latest message">
          <ChevronDownIcon size={16} />
        </button>
      ) : null}
      {retry.isError ? <Flash variant="danger" role="alert">Retry failed: {String(retry.error)}</Flash> : null}
      {(current?.queuedMessages ?? 0) > 0 ? (
        <p className="task-chat-queue" role="status">
          {current?.queuedMessages} message{current?.queuedMessages === 1 ? '' : 's'} queued
        </p>
      ) : null}
      {current === undefined ? null : (
        <>
          <Composer
            target={{ kind: 'task', taskBindingId }}
            busy={pending !== null}
            allowQueue
            initialDraft={current.messages.length === 0 ? TASK_CHAT_FIRST_PROMPT : ''}
            onSent={() => () => undefined}
          />
        </>
      )}
    </>
  );
}

/** One message of the agent's working thread: what it said, or what arrived. */
function HomeBubble({ message, roleId }: { message: HomeThreadMessage; roleId: string }): ReactNode {
  return (
    <li className={message.author === 'agent' ? 'chat-bubble chat-bubble-operator' : 'chat-bubble chat-bubble-human'}>
      <p className="chat-bubble-meta">
        <span className="chat-bubble-author">{message.author === 'agent' ? roleId : 'to the agent'}</span>
        {message.at.length > 0 ? <span className="chat-bubble-when">{chatTime(message.at)}</span> : null}
      </p>
      <p className="chat-bubble-body">{message.text}</p>
    </li>
  );
}

export interface OptimisticOperatorMessage {
  readonly id: string;
  readonly text: string;
  readonly files: readonly File[];
  readonly durableIdsAtSend: ReadonlySet<string>;
}

export function optimisticMessagesWithoutDurableMatches(
  optimistic: readonly OptimisticOperatorMessage[],
  durable: readonly OperatorChatMessage[],
): readonly OptimisticOperatorMessage[] {
  const consumed = new Set<string>();
  return optimistic.filter((entry) => {
    const match = durable.find((message) => !consumed.has(message.id)
      && !entry.durableIdsAtSend.has(message.id)
      && message.body === entry.text
      && message.attachments.length === entry.files.length
      && message.attachments.every((attachment, index) => attachment.filename === entry.files[index]?.name));
    if (match === undefined) return true;
    consumed.add(match.id);
    return false;
  });
}

function OptimisticBubble({ message }: { message: OptimisticOperatorMessage }): ReactNode {
  return (
    <li className="chat-bubble chat-bubble-human chat-bubble-optimistic">
      <p className="chat-bubble-meta"><span className="chat-bubble-author">you</span><span className="chat-bubble-when">sending…</span></p>
      {message.text.length > 0 ? <p className="chat-bubble-body">{message.text}</p> : null}
      {message.files.length === 0 ? null : (
        <div className="chat-bubble-attachments">
          {message.files.map((file, index) => <LocalAttachment key={`${file.name}-${String(index)}`} file={file} />)}
        </div>
      )}
    </li>
  );
}

function LocalAttachment({ file }: { file: File }): ReactNode {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  if (file.type.startsWith('image/') && url !== null) return <img src={url} alt={file.name} className="chat-image" />;
  return <span className="chat-file">{file.name} <span className="chat-file-size">({formatBytes(file.size)})</span></span>;
}

function Bubble({ message, onRetry, retryPending = false }: {
  message: OperatorChatMessage;
  onRetry?: () => void;
  retryPending?: boolean;
}): ReactNode {
  return (
    <li className={`chat-bubble chat-bubble-${message.author}`}>
      <p className="chat-bubble-meta">
        <span className="chat-bubble-author">
          {message.author === 'human'
            ? 'you'
            : message.author === 'operator'
              ? message.authorName === 'main-operator'
                ? 'operator'
                : message.authorName
              : 'platform'}
        </span>
        <span className="chat-bubble-when">{chatTime(message.at)}</span>
      </p>
      {message.body.length > 0 ? <p className="chat-bubble-body">{message.body}</p> : null}
      {message.attachments.length > 0 ? (
        <div className="chat-bubble-attachments">
          {message.attachments.map((attachment) => (
            <Attachment key={attachment.id} attachment={attachment} />
          ))}
        </div>
      ) : null}
      {message.delivery?.state === 'failed' ? (
        <div className="chat-delivery-failed" role="alert">
          <span>Delivery failed{message.delivery.error === null ? '.' : `: ${message.delivery.error}`}</span>
          {onRetry === undefined ? null : <Button size="small" onClick={onRetry} disabled={retryPending}>Retry</Button>}
        </div>
      ) : null}
    </li>
  );
}

function Attachment({ attachment }: { attachment: OperatorChatAttachmentView }): ReactNode {
  const url = useAttachmentUrl(attachment.digest);
  if (url === null) return <span className="hint">loading {attachment.filename}…</span>;

  if (attachment.kind === 'voice' || attachment.mediaType.startsWith('audio/')) {
    return (
      <span className="chat-voice">
        {/* The recording plays in place; captions do not exist for a voice note. */}
        <audio controls src={url} preload="metadata" />
      </span>
    );
  }
  if (attachment.mediaType.startsWith('image/')) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="chat-image-link">
        <img src={url} alt={attachment.filename} className="chat-image" />
      </a>
    );
  }
  return (
    <a href={url} download={attachment.filename} className="chat-file">
      {attachment.filename} <span className="chat-file-size">({formatBytes(attachment.sizeBytes)})</span>
    </a>
  );
}

export function taskComposerState(input: {
  readonly hasContent: boolean;
  readonly sendPending: boolean;
  readonly replying: boolean;
  readonly allowQueue: boolean;
}): { readonly canSend: boolean; readonly showStop: boolean; readonly showSend: boolean } {
  return {
    canSend: input.hasContent && !input.sendPending && (!input.replying || input.allowQueue),
    showStop: input.replying,
    showSend: !input.replying || input.allowQueue,
  };
}

type ComposerTarget =
  | { readonly kind: 'operator'; readonly chatId: string }
  | { readonly kind: 'task'; readonly taskBindingId: string };

export type TaskDraft = {
  readonly messageId: string;
  readonly content: string;
  readonly files: readonly File[];
};

export function composerStillMatchesTaskDraft(
  draft: string,
  files: readonly File[],
  retried: TaskDraft,
): boolean {
  return draft === retried.content &&
    files.length === retried.files.length &&
    files.every((file, index) => file === retried.files[index]);
}

export function composerIsEmpty(draft: string, files: readonly File[]): boolean {
  return draft.length === 0 && files.length === 0;
}

/** Board cards and selected text may cross the composer; only OS file drags belong here. */
export function isFileDrag(types: Iterable<string>): boolean {
  return Array.from(types).some((type) => type.toLowerCase() === 'files');
}

/** The shared full composer. Task Chat additionally accepts queued messages. */
function Composer({
  target,
  busy,
  allowQueue = false,
  initialDraft = '',
  onSent,
}: {
  target: ComposerTarget;
  busy: boolean;
  allowQueue?: boolean;
  initialDraft?: string;
  onSent: (text: string, files: readonly File[]) => () => void;
}): ReactNode {
  const [draft, setDraft] = useState(initialDraft);
  const [files, setFiles] = useState<readonly File[]>([]);
  const draftRef = useRef(draft);
  const filesRef = useRef(files);
  const seededDraftRef = useRef(initialDraft);
  const composerRevision = useRef(0);
  draftRef.current = draft;
  filesRef.current = files;
  const [failedTaskDraft, setFailedTaskDraft] = useState<TaskDraft | null>(null);
  /*
   * A stop click flips the button back optimistically. The honest state
   * follows a poll later — and if there was nothing to stop (a reply
   * orphaned by a daemon restart), this is also the escape hatch that
   * returns the send button instead of a stop that forever stops nothing.
   */
  const [forcedIdle, setForcedIdle] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [draggingFiles, setDraggingFiles] = useState(false);

  useEffect(() => {
    if (seededDraftRef.current === initialDraft) return;
    const next = reconcileSeededDraft(draftRef.current, seededDraftRef.current, initialDraft);
    seededDraftRef.current = initialDraft;
    if (next === draftRef.current) return;
    composerRevision.current += 1;
    draftRef.current = next;
    setDraft(next);
  }, [initialDraft]);

  const operatorChatId = target.kind === 'operator' ? target.chatId : null;
  const taskBindingId = target.kind === 'task' ? target.taskBindingId : null;
  const sendOperator = useConsoleMutation<{ content: string; files: readonly File[] }, void>(
    (data, context, args) => data.sendOperatorChatMessage(operatorChatId ?? '', args.content, context, args.files),
    [['operator-chat', operatorChatId], ['operator-chats']],
  );
  const sendTask = useConsoleMutation<TaskDraft, void>(
    (data, context, args) => data.sendTaskChatMessage(taskBindingId ?? '', args.messageId, args.content, context, args.files),
    [['task-chat', taskBindingId]],
  );
  const stopOperator = useConsoleMutation<string, void>(
    (data, context, id) => data.stopOperatorChat(id, context),
    [['operator-chat', operatorChatId]],
  );
  const stopTask = useConsoleMutation<string, void>(
    (data, context, id) => data.stopTaskChat(id, context),
    [['task-chat', taskBindingId]],
  );
  const voice = useVoiceInput((text) => setDraft((current) => {
    const next = [current.trim(), text].filter(Boolean).join(current.trim().length > 0 ? '\n' : '');
    composerRevision.current += 1;
    draftRef.current = next;
    return next;
  }));

  useEffect(() => {
    if (!busy) setForcedIdle(false);
  }, [busy]);
  const replying = busy && (target.kind === 'task' || !forcedIdle);
  const state = taskComposerState({
    hasContent: draft.trim().length > 0 || files.length > 0,
    sendPending: sendOperator.isPending || sendTask.isPending,
    replying,
    allowQueue,
  });

  const addFiles = (incoming: readonly File[]): void => {
    if (incoming.length === 0) return;
    const next = [...filesRef.current, ...incoming];
    composerRevision.current += 1;
    filesRef.current = next;
    setFiles(next);
  };

  const onFileDrop = (event: DragEvent<HTMLDivElement>): void => {
    if (!isFileDrag(event.dataTransfer.types)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDraggingFiles(false);
    addFiles(Array.from(event.dataTransfer.files));
  };

  const submit = (): void => {
    if (!state.canSend) return;
    const content = draft.trim();
    const attached = files;
    // Clear now and show the message at once — the send is durable, so waiting on
    // the round-trip only made the box sit full for a beat. On the rare failure
    // the draft comes back, so nothing a person typed is lost.
    const clearedRevision = composerRevision.current + 1;
    composerRevision.current = clearedRevision;
    draftRef.current = '';
    filesRef.current = [];
    setDraft('');
    setFiles([]);
    const removeOptimistic = onSent(content, attached);
    if (target.kind === 'task') {
      const sameFailedDraft = failedTaskDraft !== null &&
        failedTaskDraft.content === content &&
        failedTaskDraft.files.length === attached.length &&
        failedTaskDraft.files.every((file, index) => file === attached[index]);
      const taskDraft = {
        messageId: sameFailedDraft ? failedTaskDraft.messageId : crypto.randomUUID(),
        content,
        files: attached,
      } satisfies TaskDraft;
      setFailedTaskDraft(null);
      sendTask.mutate(taskDraft, {
        onError: () => {
          removeOptimistic();
          setFailedTaskDraft(taskDraft);
          if (composerRevision.current === clearedRevision && composerIsEmpty(draftRef.current, filesRef.current)) {
            draftRef.current = content;
            filesRef.current = attached;
            setDraft(content);
            setFiles(attached);
          }
        },
      });
      return;
    }
    sendOperator.mutate({ content, files: attached }, {
      onError: () => {
        removeOptimistic();
        if (composerRevision.current === clearedRevision && composerIsEmpty(draftRef.current, filesRef.current)) {
          draftRef.current = content;
          filesRef.current = attached;
          setDraft(content);
          setFiles(attached);
        }
      },
    });
  };

  return (
    <div className="chat-composer">
      {files.length > 0 ? (
        <div className="composer-chips">
          {files.map((file, index) => (
            <span className="composer-chip" key={`${file.name}-${String(index)}`}>
              {file.name}
              <button
                type="button"
                aria-label={`remove ${file.name}`}
                onClick={() => {
                  const next = files.filter((_, at) => at !== index);
                  composerRevision.current += 1;
                  filesRef.current = next;
                  setFiles(next);
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {sendOperator.isError ? (
        <Flash variant="danger" role="alert">
          The message did not send: {String(sendOperator.error)}
        </Flash>
      ) : null}
      {sendTask.isError && failedTaskDraft !== null ? (
        <Flash variant="danger" role="alert">
          The message did not send: {String(sendTask.error)}{' '}
          <Button
            variant="invisible"
            onClick={() => {
              const retrying = failedTaskDraft;
              const retryRevision = composerRevision.current;
              sendTask.mutate(retrying, {
                onSuccess: () => {
                  setFailedTaskDraft((current) => current?.messageId === retrying.messageId ? null : current);
                  if (composerRevision.current === retryRevision && composerStillMatchesTaskDraft(draftRef.current, filesRef.current, retrying)) {
                    composerRevision.current += 1;
                    draftRef.current = '';
                    filesRef.current = [];
                    setDraft('');
                    setFiles([]);
                  }
                },
              });
            }}
            disabled={sendTask.isPending}
          >
            Retry
          </Button>
        </Flash>
      ) : null}
      {voice.problem === null ? null : <Flash variant="danger" role="alert">{voice.problem}</Flash>}

      <div
        className={`composer-shell${draggingFiles ? ' composer-shell-dragging' : ''}`}
        onDragEnter={(event) => {
          if (!isFileDrag(event.dataTransfer.types)) return;
          event.preventDefault();
          dragDepth.current += 1;
          setDraggingFiles(true);
        }}
        onDragOver={(event) => {
          if (!isFileDrag(event.dataTransfer.types)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          if (!isFileDrag(event.dataTransfer.types)) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDraggingFiles(false);
        }}
        onDrop={onFileDrop}
      >
        {draggingFiles ? <span className="composer-drop-hint" role="status">Drop files to attach</span> : null}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            addFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
        <IconButton
          icon={PlusIcon}
          className="composer-plus"
          aria-label="attach files"
          title="Attach files"
          onClick={() => fileInputRef.current?.click()}
          variant="invisible"
        />
        <textarea
          className="composer-input"
          rows={draft.split('\n').length > 4 ? 4 : Math.max(1, draft.split('\n').length)}
          value={draft}
          placeholder="Message the operator — Enter sends, Shift+Enter breaks the line"
          onChange={(event) => {
            composerRevision.current += 1;
            draftRef.current = event.target.value;
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />
        {voice.supported ? (
          <IconButton
            icon={UnmuteIcon}
            className={`composer-mic${voice.recording ? ' composer-mic-recording' : ''}${voice.pending ? ' composer-mic-pending' : ''}`}
            aria-label={voice.pending ? 'transcribing voice input' : voice.recording ? 'stop voice input' : 'start voice input'}
            title={voice.pending ? 'Transcribing…' : voice.recording ? 'Stop recording' : 'Voice input'}
            disabled={voice.pending}
            onClick={voice.toggle}
            variant="invisible"
          />
        ) : null}
        {state.showStop ? (
          <IconButton
            icon={SquareFillIcon}
            className="composer-go composer-go-stop"
            aria-label="stop the reply"
            title="Stop"
            disabled={stopOperator.isPending || stopTask.isPending}
            onClick={() => {
              if (target.kind === 'task') {
                stopTask.mutate(target.taskBindingId);
              } else {
                setForcedIdle(true);
                stopOperator.mutate(target.chatId);
              }
            }}
          />
        ) : null}
        {state.showSend ? (
          <IconButton
            icon={ArrowUpIcon}
            className="composer-go"
            aria-label="send"
            title="Send"
            onClick={submit}
            disabled={!state.canSend}
          />
        ) : null}
      </div>
    </div>
  );
}

export function useVoiceInput(onTranscript: (text: string) => void): {
  readonly supported: boolean;
  readonly recording: boolean;
  readonly pending: boolean;
  readonly problem: string | null;
  readonly toggle: () => void;
} {
  const [recording, setRecording] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  const transcribe = useConsoleMutation<File, string>(
    (data, context, audio) => data.transcribeOperatorAudio(audio, context),
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const toggle = (): void => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    setMicError(null);
    void navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const preferred = ['audio/webm;codecs=opus', 'audio/mp4'].find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, preferred === undefined ? {} : { mimeType: preferred });
      const chunks: Blob[] = [];
      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      });
      recorder.addEventListener('stop', () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        if (!mountedRef.current) return;
        setRecording(false);
        const type = recorder.mimeType || 'audio/webm';
        const extension = type.includes('mp4') ? 'm4a' : 'webm';
        const audio = new File(chunks, `voice-${Date.now().toString()}.${extension}`, { type });
        transcribe.mutate(audio, { onSuccess: onTranscript });
      }, { once: true });
      recorder.start(250);
      setRecording(true);
    }).catch((error: unknown) => {
      setRecording(false);
      setMicError(`Microphone access failed: ${String(error)}`);
    });
  };

  const supported = typeof navigator !== 'undefined'
    && 'mediaDevices' in navigator
    && typeof MediaRecorder !== 'undefined';
  return {
    supported,
    recording,
    pending: transcribe.isPending,
    problem: micError ?? (transcribe.isError ? `Could not transcribe the recording: ${String(transcribe.error)}` : null),
    toggle,
  };
}

/** `14:07` today, `3 Aug, 14:07` before — chat time, not a log line. */
function chatTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const sameDay =
    at.getFullYear() === today.getFullYear() && at.getMonth() === today.getMonth() && at.getDate() === today.getDate();
  return sameDay ? time : `${at.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
